import React, { useMemo, useRef } from 'react';
import { Modal, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { buildPrintDocument } from '../../utils/buildPrintDocument';
import './index.scss';

// Reusable "preview then download" modal. Renders a snapshot of live DOM HTML
// inside an isolated <iframe> (true WYSIWYG — no style bleed or duplicate ids)
// and prints that exact document via the browser's native "Save as PDF". No
// external PDF/canvas libraries.
//
// The full print document is passed via the `srcDoc` prop (computed in render)
// rather than set imperatively on a ref — with destroyOnClose the iframe
// remounts each open, and a prop applies on mount without any ref-timing race.
//
// Props:
//   visible       — controls the modal
//   onCancel      — close handler
//   title         — modal title + document <title>
//   bodyHtml      — HTML string to render/print (e.g. node.innerHTML)
//   contentWidth  — optional px width so the preview matches the live layout
//   coverHtml     — optional cover-page HTML rendered as a full-bleed first page
const PdfPreviewModal = ({ visible, onCancel, title, bodyHtml, contentWidth, coverHtml }) => {
    const iframeRef = useRef(null);

    const srcDoc = useMemo(
        () => (bodyHtml == null ? '' : buildPrintDocument(bodyHtml, { title, contentWidth, coverHtml })),
        [bodyHtml, title, contentWidth, coverHtml]
    );

    const handleDownload = () => {
        const frame = iframeRef.current;
        if (!frame || !frame.contentWindow) return;
        // The iframe is already loaded (user is viewing the preview), so print now.
        frame.contentWindow.focus();
        frame.contentWindow.print();
    };

    return (
        <Modal
            className='pdf-preview-modal'
            title={title || 'Preview PDF'}
            visible={visible}
            onCancel={onCancel}
            width='90vw'
            centered
            destroyOnClose
            footer={[
                <Button key='cancel' onClick={onCancel}>Cancel</Button>,
                <Button key='download' type='primary' icon={<DownloadOutlined />} onClick={handleDownload}>
                    Download PDF
                </Button>,
            ]}
        >
            <iframe
                ref={iframeRef}
                title={title || 'PDF preview'}
                className='pdf-preview-modal__frame'
                srcDoc={srcDoc}
            />
        </Modal>
    );
};

export default PdfPreviewModal;
